import React, { useEffect, useState } from "react"
import { Box, Button, Grid, Image, Typography } from "src/UILibrary"
import { useTranslation } from "react-i18next"
import InputTextField from "src/components/atom/InputTextField"
import { validateField } from "src/modules/validation"
import * as yup from "yup"
import { OutlinedButton } from "src/components/buttons/OutlinedButton"
import FileSelectField from "src/components/atom/FileSelectField"
import { Company, Job, JobService } from "src/openapi"
import { useRecoilState } from "recoil"
import { ShowToast } from "src/states/showToast"
import { RESPONSE_CODE, SNACKBAR_TYPES } from "src/modules/const"
import { useLoginContext } from "src/hooks/useLoginContext"
import { ShowProgress } from "src/states/showProgress"
import { useMediaQuery } from "react-responsive"
import { TableHeaderItem } from "src/components/mypage/TableHeaderItem"

type Props = {
  item: Company
}

export const JobDetail = ({ item }: Props) => {
  const { t } = useTranslation("translation", { keyPrefix: "mypage.user" })

  const [, setShowToast] = useRecoilState(ShowToast)
  const [, setShowProgress] = useRecoilState(ShowProgress)

  const { accessToken, checkLoginStatus } = useLoginContext()

  const [file, setFile] = useState<string>("")
  const [title, setTitle] = useState<string>("")
  const [redirectURL, setRedirectURL] = useState<string>("")
  const [description, setDescription] = useState<string>("")

  const [fileValidationMessage, setFileValidationMessage] = useState<string>("")
  const [titleValidiationMessage, setTitleValidationMessage] = useState<string>("")
  const [redirectURLValidationMessage, setRedirectURLValidationMessage] = useState<string>("")
  const [descriptionValidationMessage, setDescriptionValidationMessage] = useState<string>("")

  const [job, setJob] = useState<Job | null>(null)
  const [jobs, setJobs] = useState<Job[]>([])
  const isTablet = useMediaQuery({ query: "(min-width: 1200px)" })

  let job_headers = [
    {
      size: 2,
      name: t("banner"),
      sortby: "",
    },
    {
      size: 3,
      name: t("job_title"),
      sortby: "",
    },
    {
      size: 3,
      name: t("redirect_url"),
      sortby: "",
    },
    {
      size: 4,
      name: t("description"),
      sortby: "",
    },
  ]
  useEffect(() => {
    handleGetList()
  }, [])

  const handleGetList = () => {
    JobService.jobList(0, 100, item.id as number)
      .then((res) => {
        if (res.status === RESPONSE_CODE.SUCCESS) {
          setJobs([...(res.data?.jobs as Job[])])
        } else {
          setShowToast({ show: true, text: t("failed_get_job"), type: SNACKBAR_TYPES.ERROR })
        }
      })
      .catch(() => {
        setShowToast({ show: true, text: t("failed_get_job"), type: SNACKBAR_TYPES.ERROR })
      })
  }

  const select = (job: Job | null) => {
    if (job == null) {
      setFile("")
      setTitle("")
      setRedirectURL("")
      setDescription("")
      setJob(null)
    } else {
      setFile(job.imageUrl as string)
      setTitle(job.title as string)
      setRedirectURL(job.linkUrl as string)
      setDescription(job.description as string)
      setJob(job)
    }

    setFileValidationMessage("")
    setTitleValidationMessage("")
    setRedirectURLValidationMessage("")
    setDescriptionValidationMessage("")
  }

  const validate = async () => {
    let validation = true

    if (file) {
      setFileValidationMessage("")
    } else {
      setFileValidationMessage(t("please_select_file"))
    }

    try {
      await validateField(
        yup.object().shape({
          title: yup.string().required(t("please_input_company_name")),
        }),
        "title",
        title
      )
      setTitleValidationMessage("")
    } catch (err: any) {
      setTitleValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          redirectURL: yup
            .string()
            .required(t("please_input_redirect_url"))
            .matches(
              /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/,
              t("please_input_correct_site_url")
            ),
        }),
        "redirectURL",
        redirectURL
      )
      setRedirectURLValidationMessage("")
    } catch (err: any) {
      setRedirectURLValidationMessage(err.message)
      validation = validation && false
    }

    try {
      await validateField(
        yup.object().shape({
          description: yup.string().required(t("please_input_company_name")),
        }),
        "description",
        description
      )
      setDescriptionValidationMessage("")
    } catch (err: any) {
      setDescriptionValidationMessage(err.message)
      validation = validation && false
    }

    return validation
  }

  const handleUpdate = async () => {
    if (!(await validate())) {
      return
    }

    if (job != null) {
      setShowProgress(true)
      JobService.updateJob(accessToken, job?.id as number, {
        imageUrl: file,
        title: title,
        linkUrl: redirectURL,
        description: description,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_update_job"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            handleGetList()
          } else {
            setShowToast({ show: true, text: t("failed_update_job"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed_update_job"), type: SNACKBAR_TYPES.ERROR })
        })
    } else {
      setShowProgress(true)
      JobService.createJob(accessToken, {
        companyId: item.id as number,
        linkUrl: redirectURL,
        title: title,
        description: description,
        imageUrl: file as string,
      })
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.CREATE_SUCCESS) {
            setShowToast({
              show: true,
              text: t("success_update_job"),
              type: SNACKBAR_TYPES.SUCCESS,
            })
            handleGetList()
            select(null)
          } else {
            setShowToast({ show: true, text: t("failed_update_job"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed_update_job"), type: SNACKBAR_TYPES.ERROR })
        })
    }
  }

  const handleRemove = () => {
    if (job != null) {
      setShowProgress(true)
      JobService.deleteJob(accessToken, job?.id as number)
        .then((res) => {
          setShowProgress(false)
          if (!checkLoginStatus(res)) {
            return
          }

          if (res.status === RESPONSE_CODE.SUCCESS) {
            setShowToast({ show: true, text: t("success_remove"), type: SNACKBAR_TYPES.SUCCESS })
            select(null)
            handleGetList()
          } else {
            setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
          }
        })
        .catch(() => {
          setShowProgress(false)
          setShowToast({ show: true, text: t("failed_remove"), type: SNACKBAR_TYPES.ERROR })
        })
    } else {
      setShowToast({ show: true, text: t("no_job_info"), type: SNACKBAR_TYPES.WARNING })
    }
  }

  return (
    <Box sx={{ py: "0px", px: "20px" }}>
      {isTablet ? (
        <Box sx={{ width: "100%" }}>
          <Grid container sx={{ mt: "0px", height: "32px", borderBottom: "1px dotted #D4D3D7" }}>
            {job_headers.map((header, index) => (
              <TableHeaderItem key={index} name={header.name} size={header.size} sortby="" />
            ))}
          </Grid>

          {jobs.length === 0 ? (
            <Grid container sx={{ mt: "0px", height: "80px", overflowY: "scroll" }}>
              <Box sx={{ display: "flex", my: "20px", width: "100%" }}>
                <Typography.SubTitleNormal
                  sx={{
                    mx: "auto",
                    fontFamily: "Noto Sans JP !important",
                    color: "gray",
                    fontSize: "12px",
                  }}
                >
                  {t("no_data")}
                </Typography.SubTitleNormal>
              </Box>
            </Grid>
          ) : (
            <Grid container sx={{ mt: "0px" }}>
              {jobs.map((j, index) => {
                return (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      className="notosans weight500"
                      color="text.primary"
                      md={2}
                      sm={12}
                      xs={12}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        borderBottom: "1px solid #D4D3D7",

                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      <Image
                        src={j.imageUrl!}
                        sx={{
                          width: "50px",
                          height: "50px",
                          display: "flex",
                          alignItems: "center",
                          "& img": { width: "38px", height: "38px" },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line"
                      color="text.primary"
                      md={3}
                      lg={3}
                      xl={3}
                      sm={12}
                      xs={12}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.title}
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line"
                      color="text.primary"
                      md={3}
                      lg={3}
                      xl={3}
                      sm={12}
                      xs={12}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.linkUrl}
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line"
                      color="text.primary"
                      md={4}
                      lg={4}
                      xl={4}
                      sm={12}
                      xs={12}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.description}
                    </Grid>
                  </React.Fragment>
                )
              })}
            </Grid>
          )}
        </Box>
      ) : (
        <Box sx={{ width: "100%" }}>
          {jobs.length === 0 ? (
            <Box sx={{ display: "flex", my: "20px", width: "100%" }}>
              <Typography.SubTitleNormal
                sx={{
                  mx: "auto",
                  fontFamily: "Noto Sans JP !important",
                  color: "gray",
                  fontSize: "12px",
                }}
              >
                {t("no_data")}
              </Typography.SubTitleNormal>
            </Box>
          ) : (
            <Grid container sx={{ mt: "0px" }}>
              {jobs.map((j, index) => {
                return (
                  <React.Fragment key={index}>
                    <Grid
                      item
                      className="notosans weight500"
                      color="text.primary"
                      md={2}
                      sm={2}
                      xs={2}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      <Image
                        src={j.imageUrl!}
                        sx={{
                          width: "50px",
                          height: "50px",
                          borderBottom: "1px solid #D4D3D7",
                          display: "flex",
                          alignItems: "center",
                          "& img": { width: "38px", height: "38px" },
                        }}
                      />
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line"
                      color="text.primary"
                      md={3}
                      sm={3}
                      xs={3}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.title}
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line"
                      color="text.primary"
                      md={3}
                      sm={3}
                      xs={3}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.linkUrl}
                    </Grid>
                    <Grid
                      item
                      className="notosans weight500 limit_line_sp"
                      color="text.primary"
                      md={4}
                      sm={4}
                      xs={4}
                      fontSize={12}
                      sx={{
                        my: "5px",
                        px: "20px",
                        height: "50px",
                        display: "flex",
                        alignItems: "center",
                        borderBottom: "1px solid #D4D3D7",
                        ":hover": { opacity: 0.8, cursor: "pointer" },
                      }}
                      onClick={() => select(j)}
                    >
                      {j?.description}
                    </Grid>
                  </React.Fragment>
                )
              })}
            </Grid>
          )}
        </Box>
      )}
      <Box sx={{ width: "100%", display: "flex", justifyContent: "end" }}>
        <Button
          variant="contained"
          sx={{
            minWidth: "140px",
            my: "10px",
            fontSize: "0.8rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => select(null)}
        >
          {t("add_job")}
        </Button>
      </Box>
      <FileSelectField
        file={file}
        handleChange={setFile}
        name={t("banner")}
        required
        validationMessage={fileValidationMessage}
        labelStyle={{ fontWeight: "400", fontFamily: "Noto Sans JP !important", fontSize: "16px" }}
      />
      <InputTextField
        name={t("job_title")}
        text={title}
        handleText={setTitle}
        required
        validationMessage={titleValidiationMessage}
        sx={{ mt: "30px" }}
      />
      <InputTextField
        name={t("redirect_url")}
        text={redirectURL}
        handleText={setRedirectURL}
        required
        validationMessage={redirectURLValidationMessage}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
      />
      <InputTextField
        name={t("description")}
        text={description}
        handleText={setDescription}
        required
        validationMessage={descriptionValidationMessage}
        sx={{ mt: "30px" }}
        inputStyle={{ width: "100%" }}
      />

      <Box display="flex" sx={{ mt: "30px" }}>
        <OutlinedButton
          caption={t("delete")}
          fontColor="#FC3317"
          fontWeight="700"
          fontSize={16}
          sx={{
            borderColor: "#FC3317",
            width: isTablet ? "240px" : "160px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: "auto",
            mr: isTablet ? "40px" : "10px",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
          }}
          onClick={() => handleRemove()}
        />
        <Button
          variant="contained"
          sx={{
            width: isTablet ? "240px" : "160px",
            py: "16px",
            fontSize: "1rem",
            fontFamily: "Noto Sans JP !important",
            lineHeight: "1rem",
            fontWeight: 700,
            ml: 0,
            mr: "auto",
            display: "flex",
            justifyContent: "center",
            borderRadius: 0,
            ":hover": {
              bgcolor: "#2DA389",
            },
          }}
          onClick={() => handleUpdate()}
        >
          {t("update")}
        </Button>
      </Box>
    </Box>
  )
}
